import { useEffect, useRef, useState } from "react";
import { Star, ArrowRight, ArrowLeft, Quotes } from "@phosphor-icons/react";
import { getGoogleReviews } from "../lib/api";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

const Stars = ({ n = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} weight="fill" className={i <= Math.round(n) ? "text-[#FBBC05]" : "text-zinc-200"} />
    ))}
  </div>
);

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export function GoogleReviews() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const railRef = useRef(null);

  useEffect(() => {
    getGoogleReviews().then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);

  const scroll = (dir) => {
    railRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  if (failed || !data || !(data.reviews || []).length) return null;

  return (
    <section className="border-t border-zinc-200 py-16 sm:py-20 bg-gradient-to-b from-[#F7F5FF] to-white" data-testid="google-reviews-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px]">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <GoogleG />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Google Reviews</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
              Loved by families we've guided over 10 years
            </h2>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-3xl font-black font-['IBM_Plex_Mono'] text-[#09090B] leading-none">
                {Number(data.rating || 0).toFixed(1)}
              </span>
              <Stars n={data.rating} size={18} />
              <span className="text-sm font-sans text-[#52525B]">
                {data.total} Google review{data.total === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll(-1)} data-testid="reviews-prev"
              className="hidden sm:flex w-10 h-10 items-center justify-center border border-zinc-300 rounded-full text-[#09090B] hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
              <ArrowLeft size={16} weight="bold" />
            </button>
            <button onClick={() => scroll(1)} data-testid="reviews-next"
              className="hidden sm:flex w-10 h-10 items-center justify-center border border-zinc-300 rounded-full text-[#09090B] hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div ref={railRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-1 px-1" data-testid="reviews-rail">
          {data.reviews.map((rv, idx) => (
            <article key={idx} data-testid={`review-card-${idx}`}
              className="snap-start shrink-0 w-[300px] sm:w-[340px] bg-white border border-zinc-200 rounded-2xl p-5 flex flex-col hover:border-[#6043EF] hover:shadow-lg transition-all duration-200">
              <Quotes size={22} weight="fill" className="text-[#6043EF]/25 mb-2" />
              <p className="text-[15px] font-sans leading-relaxed text-[#3F3F46] flex-1 line-clamp-6">
                {rv.text || "Highly recommended."}
              </p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-zinc-100">
                {rv.photo ? (
                  <img src={rv.photo} alt={rv.author} referrerPolicy="no-referrer" loading="lazy"
                    className="w-10 h-10 rounded-full object-cover bg-zinc-100" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#6043EF]/10 text-[#6043EF] font-bold text-sm flex items-center justify-center">
                    {initials(rv.author)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#09090B] truncate">{rv.author || "Google user"}</p>
                  <div className="flex items-center gap-2">
                    <Stars n={rv.rating} size={11} />
                    <span className="text-[11px] text-zinc-400">{rv.when}</span>
                  </div>
                </div>
                <GoogleG />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
